// components/shared/LocationAutocomplete.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
  Box,
  ClickAwayListener,
} from '@mui/material';
import { MapPin, Navigation, Locate, X } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

// Load Google Maps Script
let isScriptLoaded = false;
let isScriptLoading = false;
const scriptLoadCallbacks = [];

function loadGoogleMapsScript(apiKey, callback) {
  if (isScriptLoaded) { 
    callback();
    return;
  }

  if (isScriptLoading) {
    scriptLoadCallbacks.push(callback);
    return;
  }

  isScriptLoading = true;
  scriptLoadCallbacks.push(callback);

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  
  script.onload = () => {
    isScriptLoaded = true;
    isScriptLoading = false;
    scriptLoadCallbacks.forEach(cb => cb());
    scriptLoadCallbacks.length = 0;
  };

  document.head.appendChild(script);
}

// Helper: Extract city and postalCode from Google Maps address_components
function extractLocationDetails(addressComponents) {
  if (!addressComponents) return { city: null, postalCode: null };
  
  let city = null;
  let postalCode = null;
  
  for (const component of addressComponents) {
    // Try to get city from locality, administrative_area_level_3, or postal_town
    if (component.types.includes('locality')) {
      city = component.long_name;
    } else if (!city && component.types.includes('postal_town')) {
      city = component.long_name;
    } else if (!city && component.types.includes('administrative_area_level_3')) {
      city = component.long_name;
    }
    
    // Get postal code
    if (component.types.includes('postal_code')) {
      postalCode = component.long_name;
    }
  }
  
  return { city, postalCode };
}

export const LocationAutocomplete = ({
  label,
  placeholder,
  value,
  onChange,
  icon: Icon = MapPin,
  showDetectLocation = true,
  countryCode = 'fi',
  onLocationSelect,
}) => {
  const { isDark, colors } = useTheme();
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  
  const inputRef = useRef(null);
  const autocompleteService = useRef(null);
  const geocoderService = useRef(null);
  const timeoutRef = useRef(null);
  const suppressNextRef = useRef(false);
  const userIsTypingRef = useRef(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Initialize Google Maps services
  useEffect(() => {
    if (!apiKey) {
      console.warn('Google Maps API key missing');
      return;
    }

    loadGoogleMapsScript(apiKey, () => {
      if (window.google?.maps?.places) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
        geocoderService.current = new window.google.maps.Geocoder();
      }
    });
  }, [apiKey]);

  // Sync with external value (programmatic updates)
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
      setSuggestions([]);
      setIsOpen(false);
      suppressNextRef.current = true;
    }
  }, [value]);

  // Fetch predictions when input changes
  useEffect(() => {
    if (suppressNextRef.current) {
      suppressNextRef.current = false;
      setLoading(false);
      return;
    }

    if (!userIsTypingRef.current) {
      setLoading(false);
      return;
    }
  
    if (!inputValue || inputValue.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }
  
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  
    setLoading(true);
  
    timeoutRef.current = setTimeout(() => {
      if (!autocompleteService.current) {
        setLoading(false);
        return;
      }
  
      autocompleteService.current.getPlacePredictions(
        {
          input: inputValue,
          componentRestrictions: { country: countryCode },
        },
        (predictions, status) => {
          setLoading(false);
  
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
            setIsOpen(true);
          } else {
            setSuggestions([]);
            setIsOpen(false);
          }
        }
      );
    }, 300);
  
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [inputValue, countryCode]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    userIsTypingRef.current = true;
    setInputValue(newValue);
    onChange?.(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    userIsTypingRef.current = false;
    suppressNextRef.current = true;

    const address = suggestion.description;
    setInputValue(address);
    setSuggestions([]);
    setIsOpen(false);
    onChange?.(address);

    // Get detailed place info if callback provided
    if (onLocationSelect && suggestion.place_id) {
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );

      placesService.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['address_components', 'geometry', 'formatted_address'],
        },
        (place, status) => {
          if (status === 'OK' && place) {
            // Extract city and postalCode from address_components
            const { city, postalCode } = extractLocationDetails(place.address_components);
            
            const locationData = {
              address: place.formatted_address,
              latitude: place.geometry?.location?.lat(),
              longitude: place.geometry?.location?.lng(),
              placeId: suggestion.place_id,
              city: city,           // May be null
              postalCode: postalCode, // May be null
            };
            onLocationSelect(locationData);
          }
        }
      );
    }
  };

  const handleDetectLocation = () => {
    userIsTypingRef.current = false;
    setDetecting(true);

    if (!navigator.geolocation) {
      alert('Geolocation not supported');
      setDetecting(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        if (!geocoderService.current) {
          setDetecting(false);
          return;
        }

        geocoderService.current.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            setDetecting(false);

            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              suppressNextRef.current = true;
              setInputValue(address);
              onChange?.(address);

              if (onLocationSelect) {
                // Extract city and postalCode from address_components
                const { city, postalCode } = extractLocationDetails(results[0].address_components);
                
                onLocationSelect({
                  address,
                  latitude,
                  longitude,
                  placeId: results[0].place_id,
                  city: city,           // May be null
                  postalCode: postalCode, // May be null
                });
              }
            }
          }
        );
      },
      (error) => {
        setDetecting(false);
        console.error('Geolocation error:', error);
        alert('Could not detect location. Please check permissions.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleClear = () => {
    userIsTypingRef.current = false;
    setInputValue('');
    onChange?.('');
    setSuggestions([]);
    setIsOpen(false);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleFocus = () => {
    if (userIsTypingRef.current && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    userIsTypingRef.current = false;
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          ref={inputRef}
          fullWidth
          label={label}
          placeholder={placeholder}
          value={inputValue}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon size={20} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading && <CircularProgress size={20} />}
                {inputValue && !loading && (
                  <IconButton size="small" onClick={handleClear}>
                    <X size={16} />
                  </IconButton>
                )}
                {showDetectLocation && !loading && (
                  <IconButton
                    size="small"
                    onClick={handleDetectLocation}
                    disabled={detecting}
                    title="Detect current location"
                  >
                    {detecting ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Locate size={16} />
                    )}
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />

        {/* Suggestions Dropdown */}
        {isOpen && suggestions.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 1300,
              maxHeight: 300,
              overflow: 'auto',
              boxShadow: isDark
                ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                : '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            <List disablePadding>
              {suggestions.map((suggestion, index) => (
             <ListItem key={suggestion.place_id || index} disablePadding>
             <ListItemButton
               onClick={() => handleSuggestionClick(suggestion)}
               sx={{
                 '&:hover': {
                   bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                 },
               }}
             >
               <ListItemIcon sx={{ minWidth: 40 }}>
                 <Navigation size={18} style={{ color: colors.semantic.info }} />
               </ListItemIcon>
       
               <ListItemText
                 primary={suggestion.structured_formatting?.main_text || suggestion.description}
                 secondary={suggestion.structured_formatting?.secondary_text}
                 primaryTypographyProps={{ variant: 'body2' }}
                 secondaryTypographyProps={{ variant: 'caption' }}
               />
             </ListItemButton>
           </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};